import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { TEAMS, PROJECT_TYPES } from "../data/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "./ui/sonner";
import { Plus, Loader2, HardHat, PencilRuler, AlertCircle } from "lucide-react";
import { AddressInput } from "./AddressInput";
import { StaffPicker, useStaffOptions } from "./StaffPicker";
import { CoStaffPicker, emptyCoStaff, dropPrimary } from "./CoStaffPicker";
import { addMonths } from "../lib/date";
import { DrawingsPicker } from "./DrawingsPicker";
import { ProjectAddressCheck, useProjectAddressCheck } from "./ProjectAddressCheck";

const TEAM_BRANCH = { team_norcal: "norcal", team_1: "la", team_2: "la", team_3: "la", team_sd: "sd" };
const BRANCH_TEAM = { norcal: "team_norcal", la: "team_1", sd: "team_sd" };
const NATIVE_SEL = "mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });

/* initial：从已签约客户一键建项目时预填 { leadId, address, clientName, phone, email, sales, branch, type, ownerLang } */
export default function CreateProject({ open, onClose, initial }) {
  const { t, lang, api, createProject } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  const staff = useStaffOptions();
  const empty = () => {
    const i = initial || {};
    const branch = i.branch && BRANCH_TEAM[i.branch] ? i.branch : "";
    return { address: i.address || "", type: PROJECT_TYPES.some((x) => x.key === i.type) ? i.type : "ADU", stage: "construction", clientName: i.clientName || "", phone: i.phone || "", email: i.email || "",
      team: branch ? BRANCH_TEAM[branch] : "", branch, sales: i.sales || "", manager: "", onlineManager: "", designer: "", drafter: "", ownerLang: ["zh", "en", "es"].includes(i.ownerLang) ? i.ownerLang : "zh",
      expectedStart: today(), expectedEnd: addMonths(today(), 6), coStaff: emptyCoStaff(), leadId: i.leadId || "", drawings: [] };
  };
  const [form, setForm] = useState(empty);
  const addressCheck = useProjectAddressCheck(api, form.address);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => { if (open) { setForm(empty()); setTried(false); } }, [open, initial?.leadId]); // eslint-disable-line
  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "expectedStart" && (!f.expectedEnd || f.expectedEnd === addMonths(f.expectedStart, 6))) next.expectedEnd = addMonths(v, 6);
    if (k === "team" && (!f.branch || f.branch === TEAM_BRANCH[f.team])) next.branch = TEAM_BRANCH[v] || "";
    if (k in f.coStaff) next.coStaff = dropPrimary(f.coStaff, k, v);
    return next;
  });
  const setCo = (k, arr) => setForm((f) => ({ ...f, coStaff: { ...f.coStaff, [k]: arr } }));

  const missing = [
    !form.address.trim() && (zh ? "项目地址" : "address"),
    !form.team && (zh ? "分配到小组" : "team"),
    !form.clientName.trim() && (zh ? "客户姓名" : "client name"),
    form.stage === "construction" && !form.expectedStart && (zh ? "预计开工日期" : "expected start"),
  ].filter(Boolean);
  const bad = (k) => tried && ((k === "address" && !form.address.trim()) || (k === "team" && !form.team) || (k === "clientName" && !form.clientName.trim()) || (k === "expectedStart" && form.stage === "construction" && !form.expectedStart));
  const submit = async () => {
    setTried(true);
    if (missing.length) { toast.error(zh ? `请先填写：${missing.join("、")}` : `Please fill: ${missing.join(", ")}`); return; }
    if (uploading) { toast.error(zh ? "图纸还在上传中，请稍候" : "Drawings are still uploading"); return; }
    setBusy(true);
    try {
      const addressAcknowledgment = await addressCheck.check(form, "engineering");
      if (!addressAcknowledgment) { setBusy(false); return; }
      const tm = TEAMS.find((x) => x.id === form.team);
      const p = await createProject({ ...form, addressAcknowledgment, address: form.address.trim(), clientName: form.clientName.trim(), teamName: tm?.name, manager: form.manager || "-", drawings: form.drawings.map(({ fileId, filename, contentType, kind }) => ({ fileId, filename, contentType, kind })) });
      toast.success(zh ? `项目已创建：${p?.address || form.address}${form.drawings.length ? ` · 已归档 ${form.drawings.length} 份图纸` : ""}` : `Project created: ${p?.address || form.address}`);
      setForm(empty()); setTried(false);
      onClose();
      if (p?.id) nav(`/project/${p.id}`);
    } catch (e) {
      if (addressCheck.handleError(e)) { setBusy(false); return; }
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("；") : (zh ? "创建失败，请检查网络后重试" : "Create failed"), { duration: 8000 });
    }
    setBusy(false);
  };

  const L = ({ children }) => <label className="text-sm font-medium text-slate-700">{children}</label>;
  const errCls = (k) => (bad(k) ? " border-red-400 ring-1 ring-red-200" : "");
  const dirty = form.address || form.clientName || form.team || form.phone || form.email || form.drawings.length;
  const guardClose = (e) => {   // 地址建议打开时 Esc 只收起建议；已填内容时 Esc / 点外部不关弹窗（防误触丢失输入）
    if (busy || uploading || document.querySelector('[data-address-open="1"]')) { e.preventDefault(); return; }
    if (dirty) { e.preventDefault(); toast.info(zh ? "表单已有内容，请点「取消」关闭或「创建项目」提交" : "Form has content · use Cancel or Create"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="create-project-dialog" onEscapeKeyDown={guardClose} onInteractOutside={guardClose}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4 text-brand-500" />{t("createProject")}</DialogTitle>
          <DialogDescription className="text-xs">{initial?.leadId ? (zh ? `从已签约客户「${initial.clientName || ""}」创建施工项目：地址 / 客户 / 销售 / 分公司已预填，创建后自动关联该客户并接管其付款计划。` : "Pre-filled from the signed customer; the project is linked to the customer on creation.") : (zh ? "带 * 为必填；客户已有图纸的项目选「直接进入施工」，创建后立即出现在工程项目与项目主页。" : "* required · pick “Construction” when the client already has drawings.")}</DialogDescription>
        </DialogHeader>
        <ProjectAddressCheck state={addressCheck} onLeave={onClose} />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2" data-testid="create-project-stage" data-stage={form.stage}>
            {[["construction", HardHat, zh ? "客户已有图纸 · 直接进入施工" : "Has drawings · construction", zh ? "立即进入施工阶段（前期准备）" : "Starts at pre-construction"],
              ["design", PencilRuler, zh ? "需要先设计 · 进入设计阶段" : "Needs design first", zh ? "设计完成后再转入施工" : "Hands off to construction later"]].map(([k, Icon, title, sub]) => (
              <button key={k} type="button" onClick={() => set("stage", k)} data-testid={`create-project-stage-${k}`} data-on={form.stage === k ? "1" : "0"}
                className={`text-left rounded-lg border px-3 py-2 transition-colors ${form.stage === k ? "border-brand-400 bg-brand-50 ring-1 ring-brand-200" : "border-slate-200 hover:bg-slate-50"}`}>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800"><Icon className={`h-4 w-4 ${form.stage === k ? "text-brand-600" : "text-slate-400"}`} />{title}</span>
                <span className="block text-[11px] text-slate-500 mt-0.5">{sub}</span>
              </button>
            ))}
          </div>
          <div>
            <L>{t("address")} *</L>
            <AddressInput value={form.address} onChange={(v) => set("address", v)} className={`mt-1${errCls("address")}`} placeholder="123 Main St, City, CA 90000" testid="create-project-address"
              onSelect={(d) => setForm((f) => ({ ...f, address: d.formattedAddress || f.address, ...(d.branch && (staff.branches || []).some((b) => b.id === d.branch) ? { branch: d.branch } : {}) }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>{t("projectType")}</L>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger className="mt-1" data-testid="create-project-type"><SelectValue placeholder={t("selectType")} /></SelectTrigger>
                <SelectContent>{PROJECT_TYPES.map((x) => <SelectItem key={x.key} value={x.key}>{zh ? x.zh : x.en}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <L>{zh ? "业主首选语言" : "Owner language"}</L>
              <select value={form.ownerLang} onChange={(e) => set("ownerLang", e.target.value)} data-testid="create-project-owner-lang" data-no-i18n className={NATIVE_SEL}>
                <option value="zh">中文</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>{t("assignTeam")} *</L>
              <select value={form.team} onChange={(e) => set("team", e.target.value)} data-testid="create-project-team" className={NATIVE_SEL + errCls("team")}>
                <option value="">{t("selectTeam")}</option>
                {TEAMS.map((x) => <option key={x.id} value={x.id}>{zh ? x.zh : x.name}</option>)}
              </select>
            </div>
            <div>
              <L>{zh ? "所属分公司" : "Branch"}</L>
              <select value={form.branch} onChange={(e) => set("branch", e.target.value)} data-testid="create-project-branch" className={NATIVE_SEL}>
                <option value="">{zh ? "按施工组自动" : "Auto by crew"}</option>
                {(staff.branches || []).map((b) => <option key={b.id} value={b.id}>{zh ? b.zh : b.en}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><L>{t("clientName")} *</L><Input value={form.clientName} onChange={(e) => set("clientName", e.target.value)} className={`mt-1${errCls("clientName")}`} data-testid="create-project-client" /></div>
            <div><L>{t("phone")}</L><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" data-testid="create-project-phone" /></div>
          </div>
          <div><L>{t("email")}</L><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="create-project-email" /></div>

          <DrawingsPicker zh={zh} value={form.drawings} onChange={(upd) => setForm((f) => ({ ...f, drawings: typeof upd === "function" ? upd(f.drawings) : upd }))} onBusy={setUploading} disabled={busy} />

          <div className="rounded-lg border border-slate-100 p-3 space-y-3" data-testid="create-project-staff">
            <p className="text-sm font-medium text-slate-700">{zh ? "关键人员（仅在职员工，可稍后在项目主页补填）" : "Key staff (active employees only · optional)"}
              <span className="ml-1.5 text-[10px] font-normal text-slate-400">{zh ? "美国岗位可添加最多 3 名协办同事" : "US roles: up to 3 co-assist colleagues"}</span></p>
            <div className="grid grid-cols-2 gap-3">
              <div><L>{zh ? "场地项目经理" : "Field PM"}</L><StaffPicker value={form.manager} onChange={(v) => set("manager", v)} options={staff.pms} lang={lang} testid="create-project-manager" placeholder={zh ? "选择项目经理" : "Select PM"} />
                <CoStaffPicker value={form.coStaff.manager} onChange={(a) => setCo("manager", a)} options={staff.pms} primary={form.manager} lang={lang} testid="create-project-co-manager" /></div>
              <div><L>{zh ? "线上项目经理" : "Online PM"}</L><StaffPicker value={form.onlineManager} onChange={(v) => set("onlineManager", v)} options={staff.pms} lang={lang} testid="create-project-online-manager" placeholder={zh ? "选择线上项目经理" : "Select online PM"} />
                <CoStaffPicker value={form.coStaff.onlineManager} onChange={(a) => setCo("onlineManager", a)} options={staff.pms} primary={form.onlineManager} lang={lang} testid="create-project-co-online-manager" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><L>{zh ? "美国建筑设计师" : "US Architect"}</L><StaffPicker value={form.designer} onChange={(v) => set("designer", v)} options={staff.architects} lang={lang} testid="create-project-designer" placeholder={zh ? "选择美国建筑设计师" : "Select US architect"} />
                <CoStaffPicker value={form.coStaff.designer} onChange={(a) => setCo("designer", a)} options={staff.architects} primary={form.designer} lang={lang} testid="create-project-co-designer" /></div>
              <div><L>{zh ? "中国建筑设计师" : "CN Architect"}</L><StaffPicker value={form.drafter} onChange={(v) => set("drafter", v)} options={staff.drafters} lang={lang} testid="create-project-drafter" placeholder={zh ? "选择中国建筑设计师" : "Select CN architect"} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><L>{t("salesperson")}</L><StaffPicker value={form.sales} onChange={(v) => set("sales", v)} options={staff.sales} lang={lang} testid="create-project-sales" placeholder={t("selectSales")} /></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><L>{t("expectedStart")}{form.stage === "construction" ? " *" : ""}</L><Input type="date" value={form.expectedStart} onChange={(e) => set("expectedStart", e.target.value)} className={`mt-1${errCls("expectedStart")}`} data-testid="create-project-start" /></div>
            <div><L>{t("expectedEnd")}</L><Input type="date" value={form.expectedEnd} onChange={(e) => set("expectedEnd", e.target.value)} className="mt-1" data-testid="create-project-end" /></div>
          </div>
          {tried && missing.length > 0 && (
            <p className="text-xs text-red-600 flex items-center gap-1" data-testid="create-project-missing"><AlertCircle className="h-3.5 w-3.5" />{zh ? `还需填写：${missing.join("、")}` : `Still missing: ${missing.join(", ")}`}</p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={busy} data-testid="create-project-cancel">{t("cancel")}</Button>
            <Button onClick={submit} disabled={busy} className="bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-50" data-testid="create-project-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Plus className="h-4 w-4 mr-1" />}{busy ? (zh ? "创建中…" : "Creating…") : t("createProject")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
